import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Minus, Plus, Truck, ShieldCheck, RotateCcw, ChevronRight, BadgeCheck } from "lucide-react";
import { PRODUCTS, productBySlug, formatPrice } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { FadeUp, ScaleIn } from "@/components/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const ProductPage = () => {
  const { slug } = useParams();
  const { addItem, setDrawerOpen } = useCart();
  const [activeImage, setActiveImage] = useState(0);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const product = productBySlug(slug);

  useEffect(() => {
    setActiveImage(0);
    setVariantId(null);
    setQty(1);
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) return <Navigate to={`/produit/${PRODUCTS[0].slug}`} replace />;
  const variant = product.variants.find((v) => v.id === variantId) || product.variants.find((v) => v.popular) || product.variants[0];

  const handleAdd = () => {
    addItem(variant.id, qty);
    setDrawerOpen(true);
    toast.success("Ajouté à votre panier ✦");
  };

  return (
    <div data-testid="product-page" className="pt-28 sm:pt-36 pb-24 bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <nav data-testid="product-breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-warm mb-10">
          <Link to="/" className="hover:text-terracotta transition-colors">Accueil</Link>
          <ChevronRight size={12} />
          <span className="text-onyx font-medium">{product.shortName}</span>
        </nav>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Gallery */}
          <div className="lg:sticky lg:top-28 self-start">
            <ScaleIn>
              <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-rose-gold/20 bg-blush">
                <img
                  data-testid="product-main-image"
                  src={product.images[activeImage]}
                  alt={product.name}
                  className="w-full h-[24rem] sm:h-[32rem] object-cover transition-all duration-500"
                />
              </div>
            </ScaleIn>
            <div className="mt-5 grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumbnail-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`overflow-hidden rounded-2xl border-2 transition-all ${activeImage === i ? "border-terracotta shadow-md" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={img} alt={`Vue ${i + 1}`} className="w-full h-20 sm:h-24 object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <FadeUp delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#2E6B4F] inline-block animate-pulse" /> En stock — expédié sous 24h
            </p>
            <h1 data-testid="product-title" className="font-serif text-4xl sm:text-5xl font-light text-onyx leading-tight">{product.name}</h1>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center gap-0.5" data-testid="product-rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} className="fill-rose-gold text-rose-gold" />
                ))}
              </div>
              <p className="text-sm text-muted-warm">{product.rating}/5 · {product.reviewsCount.toLocaleString("fr-FR")} avis</p>
            </div>
            <p className="mt-6 text-sm sm:text-base text-muted-warm leading-relaxed">{product.description}</p>

            <div className="mt-8 space-y-3" data-testid="product-variant-selector">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  data-testid={`product-variant-${v.id}`}
                  onClick={() => setVariantId(v.id)}
                  className={`w-full flex items-center justify-between rounded-2xl border px-6 py-4 transition-all ${
                    variantId === v.id
                      ? "border-terracotta bg-white shadow-lg shadow-rose-gold/15"
                      : "border-blush-deep bg-white/60 hover:border-rose-gold"
                  }`}
                >
                  <div className="text-left">
                    <p className="font-semibold text-onyx text-sm flex items-center gap-2">
                      {v.label}
                      {v.popular && <span className="text-[10px] font-mono uppercase bg-rose-gold/20 text-terracotta rounded-full px-2 py-0.5">Le + choisi</span>}
                    </p>
                    <p className="text-xs text-muted-warm mt-0.5">{v.sublabel} · {v.perUnit}</p>
                  </div>
                  <div className="text-right">
                    {v.compareAt && <p className="text-xs text-stone-400 line-through">{formatPrice(v.compareAt)}</p>}
                    <p className="font-serif text-xl text-terracotta">{formatPrice(v.price)}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-7 flex items-stretch gap-4">
              <div className="flex items-center gap-3 border border-blush-deep bg-white rounded-full px-4">
                <button data-testid="product-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="text-onyx hover:text-terracotta transition-colors"><Minus size={16} /></button>
                <span data-testid="product-qty-value" className="font-mono text-base w-6 text-center">{qty}</span>
                <button data-testid="product-qty-increase" onClick={() => setQty(Math.min(20, qty + 1))} className="text-onyx hover:text-terracotta transition-colors"><Plus size={16} /></button>
              </div>
              <button
                data-testid="product-add-to-cart-button"
                onClick={handleAdd}
                className="flex-1 rounded-full bg-terracotta text-cream py-4 text-sm font-semibold tracking-wide hover:bg-onyx transition-colors"
              >
                Ajouter au panier — {formatPrice(variant.price * qty)}
              </button>
            </div>

            <div className="mt-7 grid grid-cols-3 gap-3 text-center">
              {[
                { icon: Truck, label: "Livraison offerte dès 25 €" },
                { icon: ShieldCheck, label: "CB & PayPal sécurisés" },
                { icon: RotateCcw, label: "Retour 30 jours" },
              ].map((g, i) => (
                <div key={i} data-testid={`product-guarantee-${i}`} className="bg-blush rounded-2xl px-3 py-4 flex flex-col items-center gap-2">
                  <g.icon size={18} className="text-terracotta" />
                  <p className="text-[11px] text-muted-warm leading-tight">{g.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Accordion type="single" collapsible className="space-y-3" data-testid="product-accordions">
                <AccordionItem value="usage" className="bg-white rounded-2xl border border-blush-deep px-6">
                  <AccordionTrigger data-testid="product-accordion-usage" className="font-medium text-onyx text-sm py-5">Conseils d'utilisation</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <ol className="space-y-3">
                      {product.usage.map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-muted-warm">
                          <span className="font-mono text-xs text-terracotta bg-blush rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="benefits" className="bg-white rounded-2xl border border-blush-deep px-6">
                  <AccordionTrigger data-testid="product-accordion-benefits" className="font-medium text-onyx text-sm py-5">Les bienfaits</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <ul className="space-y-2.5">
                      {product.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-muted-warm">
                          <BadgeCheck size={15} className="text-terracotta mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="inci" className="bg-white rounded-2xl border border-blush-deep px-6">
                  <AccordionTrigger data-testid="product-accordion-inci" className="font-medium text-onyx text-sm py-5">Composition (INCI)</AccordionTrigger>
                  <AccordionContent className="pb-6 text-xs text-muted-warm leading-relaxed">{product.inci}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="certif" className="bg-white rounded-2xl border border-blush-deep px-6">
                  <AccordionTrigger data-testid="product-accordion-certif" className="font-medium text-onyx text-sm py-5">Certification dermatologique</AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm text-muted-warm leading-relaxed">
                    Formule testée sous contrôle dermatologique, sans parabène, sans sulfate et non comédogène. Convient aux peaux sensibles. Fabriqué selon les normes cosmétiques européennes (CPNP).
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </FadeUp>
        </div>
      </div>

      {/* REVIEWS */}
      <div data-testid="reviews-section" className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <FadeUp>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Avis Clientes Vérifiés</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-light text-onyx">Elles l'ont adopté.</h2>
            </div>
            <div className="flex items-center gap-4 bg-white rounded-2xl border border-blush-deep px-6 py-4">
              <p className="font-serif text-4xl text-terracotta">{product.rating}</p>
              <div>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className={i < Math.round(product.rating) ? "fill-rose-gold text-rose-gold" : "fill-blush text-blush"} />
                  ))}
                </div>
                <p className="text-xs text-muted-warm mt-1">{product.reviewsCount.toLocaleString("fr-FR")} avis vérifiés</p>
              </div>
            </div>
          </div>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-6">
          {product.reviews.map((r, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div data-testid={`review-card-${i}`} className="bg-white rounded-3xl border border-blush-deep p-7 h-full flex flex-col hover:shadow-lg hover:shadow-rose-gold/10 transition-shadow duration-500">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={14} className={s < Math.floor(r.rating) ? "fill-rose-gold text-rose-gold" : "fill-blush text-blush"} />
                  ))}
                </div>
                <p className="font-serif italic text-base text-onyx leading-relaxed flex-1">« {r.text} »</p>
                <div className="mt-5 pt-5 border-t border-blush-deep flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blush flex items-center justify-center font-serif text-terracotta">{r.name[0]}</div>
                  <div>
                    <p className="text-sm font-semibold text-onyx">{r.name}</p>
                    <p className="text-[11px] text-muted-warm flex items-center gap-1">
                      <BadgeCheck size={11} className="text-[#2E6B4F]" /> Achat vérifié · {r.city} · {r.date}
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      {/* CROSS-SELL */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <FadeUp>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-onyx mb-10">Complétez votre rituel</h2>
        </FadeUp>
        <div className="grid sm:grid-cols-3 gap-6">
          {PRODUCTS.filter((p) => p.slug !== product.slug).map((p, i) => (
            <FadeUp key={p.slug} delay={i * 0.1}>
              <Link
                to={`/produit/${p.slug}`}
                data-testid={`crosssell-${p.slug}`}
                className="group block bg-white rounded-3xl border border-blush-deep overflow-hidden hover:shadow-xl hover:shadow-rose-gold/10 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="overflow-hidden h-56">
                  <img src={p.images[0]} alt={p.shortName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="font-serif text-xl text-onyx">{p.shortName}</p>
                  <p className="text-xs text-muted-warm mt-1.5 leading-relaxed line-clamp-2">{p.tagline}</p>
                  <p className="mt-3 text-sm font-semibold text-terracotta">dès {formatPrice(p.variants[0].price)}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
